import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  SECTION_CONFIG,
  SECTION_QUESTIONS,
  COMPLETION_SECTION_A,
  COMPLETION_SECTION_B,
  CLOSURE_A_CARDS,
  CLOSURE_B_CARDS,
} from "../data/diagnosticContent";
import { ArrowLeft, ArrowRight, Shield, Lock, Clock, Check, Compass, Link2, ShieldCheck, Award, Trophy, BarChart2, Building2, Zap, Rocket, MessageCircle, FileText, Target, Eye } from "lucide-react";
import { brandAssets } from "../components/pivotbox/siteContent";

/* ─── helpers ─── */
function shuffle(arr) {
  const c = [...arr];
  for (let i = c.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [c[i], c[j]] = [c[j], c[i]];
  }
  return c;
}

function renderIntroBody(text) {
  return text.split("\n\n").map((para, i) => {
    if (para.startsWith("> ")) {
      return <div key={i} className="diag-blockquote">{para.slice(2)}</div>;
    }
    const parts = para.split(/(\*\*.*?\*\*)/g);
    return (
      <p key={i}>
        {parts.map((part, j) =>
          part.startsWith("**") && part.endsWith("**")
            ? <strong key={j}>{part.slice(2, -2)}</strong>
            : part
        )}
      </p>
    );
  });
}

const TOTAL_QUESTIONS = Object.values(SECTION_QUESTIONS).reduce((s, q) => s + q.length, 0);

const SECTION_NAV = SECTION_CONFIG.map((s) => ({ id: s.id, tag: s.tag, shortTitle: s.title, accent: s.accent, accentLight: s.accentLight }));

/* ─── SCREENS ─── */
const SCREEN = { LOGIN: 0, OTP: 1, OVERVIEW: 6, INTRO: 2, QUESTION: 3, BREAK: 4, COMPLETE: 5, CLOSURE: 7 };

export default function DiagnosticPage() {
  const [screen, setScreen] = useState(SCREEN.LOGIN);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [allAnswers, setAllAnswers] = useState({});
  const [maxSectionReached, setMaxSectionReached] = useState(0);

  // Premium-access token (from /find-out → Razorpay → /diagnostic?token=xxx)
  const [accessToken, setAccessToken] = useState(null);

  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loginError, setLoginError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);

  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [phase, setPhase] = useState("idle");
  const t1 = useRef(null);
  const t2 = useRef(null);

  // Per-respondent session ID — generated once and persisted in localStorage
  // so it survives refresh and is the same across all 4 section submissions.
  const [sessionId] = useState(() => {
    try {
      const KEY = "pivotbox_diag_session_id";
      let sid = localStorage.getItem(KEY);
      if (!sid) {
        sid = (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID()
          : `sid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(KEY, sid);
      }
      return sid;
    } catch {
      return `sid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
  });

  useEffect(() => { window.scrollTo(0, 0); }, [screen]);
  useEffect(() => () => { clearTimeout(t1.current); clearTimeout(t2.current); }, []);

  // Premium-access token validation state — drives the paywall gate.
  //   null    = not yet checked (loading spinner)
  //   "valid" = paid + token still good → render the new welcome screen
  //   "denied" = no token in URL OR token invalid/expired → render paywall message
  const [tokenStatus, setTokenStatus] = useState(null);

  // ── Premium access token validation (Razorpay → /diagnostic?token=xxx) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get("token");
    if (!tok) {
      setTokenStatus("denied");
      return;
    }
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/payments/token/${encodeURIComponent(tok)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data && data.valid) {
          setAccessToken({ token: tok, ...data });
          if (data.name && !name) setName(data.name);
          if (data.mobile && !mobile) setMobile(data.mobile);
          setTokenStatus("valid");
        } else {
          setTokenStatus("denied");
        }
      })
      .catch(() => setTokenStatus("denied"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [resendTimer]);

  // ── Auto-save to localStorage on every answer change ──
  // Storage key includes question-bank version so old partial sessions don't
  // hydrate stale answers against the new master-bank questions.
  useEffect(() => {
    if (!mobile || Object.keys(allAnswers).length === 0) return;
    const key = `pivotbox_diag_v2_${mobile}`;
    const data = { allAnswers, sectionIdx, maxSection: maxSectionReached, name, ts: Date.now() };
    try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
  }, [allAnswers, sectionIdx, mobile, maxSectionReached, name]);

  // ── Auto-save partial progress to backend + Google Sheet (debounced 1.5s after last answer) ──
  // Captures in-progress diagnostics so partial completions are visible in the sheet even when
  // a respondent rates a few questions and never submits the section.
  useEffect(() => {
    if (!mobile || screen === SCREEN.LOGIN || screen === SCREEN.OTP) return;
    const answeredCount = Object.values(allAnswers).reduce((s, a) => s + Object.keys(a).length, 0);
    if (answeredCount === 0) return;
    const t = setTimeout(() => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        fetch(`${API_URL}/api/diagnostic/progress`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            mobile: mobile,
            name: name.trim(),
            email: (accessToken && accessToken.email) || "",
            answers_by_section: allAnswers,
          }),
        }).catch((err) => console.error("Progress save failed:", err));
      } catch (err) { console.error("Progress save failed:", err); }
    }, 1500);
    return () => clearTimeout(t);
  }, [allAnswers, mobile, name, sessionId, screen]);

  // ── Restore from localStorage after OTP verified ──
  const restoreProgress = useCallback((mob) => {
    try {
      const key = `pivotbox_diag_v2_${mob}`;
      const raw = localStorage.getItem(key);
      if (!raw) return false;
      const data = JSON.parse(raw);
      if (!data.allAnswers || Object.keys(data.allAnswers).length === 0) return false;
      // Only restore if saved within 7 days
      if (Date.now() - data.ts > 7 * 24 * 60 * 60 * 1000) { localStorage.removeItem(key); return false; }
      setAllAnswers(data.allAnswers);
      setSectionIdx(data.sectionIdx || 0);
      setMaxSectionReached(data.maxSection || data.sectionIdx || 0);
      if (data.name && !name) setName(data.name);
      return true;
    } catch { return false; }
  }, [name]);

  const sec = SECTION_CONFIG[sectionIdx];
  const secId = sec?.id;
  const sectionAnswers = useMemo(() => allAnswers[secId] || {}, [allAnswers, secId]);
  const answeredCount = Object.keys(sectionAnswers).length;
  const currentQ = questions[qIdx];
  const progress = questions.length ? (answeredCount / questions.length) * 100 : 0;
  const allAnswered = answeredCount === questions.length && questions.length > 0;

  const totalAnswered = useMemo(() => Object.values(allAnswers).reduce((s, a) => s + Object.keys(a).length, 0), [allAnswers]);

  // First name extracted from full name — used to personalise OTP / Overview / Break / Closure screens
  const firstName = useMemo(() => {
    const trimmed = (name || "").trim();
    if (!trimmed) return "";
    const first = trimmed.split(/\s+/)[0];
    // Capitalise first letter (e.g. "anil" → "Anil"); leave rest of casing as the user typed it
    return first.charAt(0).toUpperCase() + first.slice(1);
  }, [name]);

  /* ─── LOGIN ─── */
  const handleSendOtp = useCallback(() => {
    if (!name.trim() || name.trim().length < 2) { setLoginError("Please enter your full name."); return; }
    if (mobile.replace(/\D/g, "").length < 10) { setLoginError("Please enter a valid 10-digit mobile number."); return; }
    setLoginError("");
    // Fire-and-forget: capture this respondent's intent (name + mobile + session_id) the
    // moment they click "Send OTP". This creates a row in the Google Sheet so partial
    // completions are visible — not just respondents who finish all 4 sections.
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      fetch(`${API_URL}/api/diagnostic/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), mobile, session_id: sessionId }),
      }).catch((err) => console.error("Failed to log diagnostic start:", err));
    } catch (err) {
      console.error("Failed to log diagnostic start:", err);
    }
    setScreen(SCREEN.OTP);
    setResendTimer(60);
    setTimeout(() => otpRefs.current[0]?.focus(), 100);
  }, [mobile, name, sessionId]);

  const handleOtpChange = useCallback((idx, val) => {
    if (val.length > 1) val = val.slice(-1);
    if (val && !/^\d$/.test(val)) return;
    setOtp((prev) => { const next = [...prev]; next[idx] = val; return next; });
    if (val && idx < 5) setTimeout(() => otpRefs.current[idx + 1]?.focus(), 30);
  }, []);

  const handleOtpKeyDown = useCallback((idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) setTimeout(() => otpRefs.current[idx - 1]?.focus(), 30);
  }, [otp]);

  const handleVerifyOtp = useCallback(() => {
    if (otp.join("").length < 6) { setLoginError("Please enter the 6-digit OTP."); return; }
    setLoginError("");
    const hasProgress = restoreProgress(mobile);
    if (!hasProgress) setSectionIdx(0);
    setScreen(SCREEN.OVERVIEW);
  }, [otp, mobile, restoreProgress]);

  /* ─── PAID-USER START (skips OTP — they're verified via Razorpay token) ─── */
  const handlePaidStart = useCallback(() => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      fetch(`${API_URL}/api/diagnostic/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: (name || (accessToken && accessToken.name) || "Paid user").trim(),
          mobile: mobile || (accessToken && accessToken.mobile) || "",
          email: (accessToken && accessToken.email) || "",
          session_id: sessionId,
          source: "razorpay",
          access_token: accessToken && accessToken.token,
        }),
      }).catch((err) => console.error("Failed to log paid diagnostic start:", err));
    } catch (err) {
      console.error("Failed to log paid diagnostic start:", err);
    }
    const hasProgress = restoreProgress(mobile);
    if (!hasProgress) setSectionIdx(0);
    setScreen(SCREEN.OVERVIEW);
  }, [name, mobile, sessionId, accessToken, restoreProgress]);

  // Store shuffled questions per section so going back preserves order
  const [sectionQuestionsCache, setSectionQuestionsCache] = useState({});

  /* ─── SECTION FLOW ─── */
  const startSection = useCallback(() => {
    let qs = sectionQuestionsCache[sec.id];
    if (!qs) {
      qs = shuffle(SECTION_QUESTIONS[sec.id].map((q) => ({ ...q })));
      setSectionQuestionsCache((prev) => ({ ...prev, [sec.id]: qs }));
    }
    setQuestions(qs);
    setQIdx(0);
    setPhase("idle");
    setScreen(SCREEN.QUESTION);
  }, [sec, sectionQuestionsCache]);

  const animateTo = useCallback((nextIdx) => {
    clearTimeout(t1.current); clearTimeout(t2.current);
    setPhase("out");
    t1.current = setTimeout(() => { setQIdx(nextIdx); setPhase("in"); t2.current = setTimeout(() => setPhase("idle"), 200); }, 180);
  }, []);

  const handleRate = useCallback((rating) => {
    if (!currentQ) return;
    setAllAnswers((prev) => ({ ...prev, [secId]: { ...(prev[secId] || {}), [currentQ.code]: rating } }));
    // No auto-advance — user clicks Next manually
  }, [currentQ, secId]);

  const handleBack = useCallback(() => {
    if (qIdx === 0 || phase !== "idle") return;
    clearTimeout(t1.current); clearTimeout(t2.current);
    setQIdx((i) => i - 1);
    setPhase("in");
    t2.current = setTimeout(() => setPhase("idle"), 200);
  }, [qIdx, phase]);

  const handleNext = useCallback(() => {
    if (qIdx >= questions.length - 1 || phase !== "idle") return;
    // Block next if current question is not answered
    if (currentQ && !sectionAnswers[currentQ.code]) return;
    animateTo(qIdx + 1);
  }, [qIdx, questions.length, phase, animateTo, currentQ, sectionAnswers]);

  const handleSubmitSection = useCallback(async () => {
    if (!allAnswered) return;
    const currentSecId = sec.id;
    const currentAnswers = allAnswers[currentSecId] || {};

    // Save section to backend
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/diagnostic/section`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name,
          mobile: mobile,
          email: (accessToken && accessToken.email) || "",
          section_id: currentSecId,
          answers: currentAnswers,
          session_id: sessionId,
        }),
      });
    } catch (err) {
      console.error("Failed to save section:", err);
    }

    if (sectionIdx < SECTION_CONFIG.length - 1 && sec.breakHeadline) setScreen(SCREEN.BREAK);
    else setScreen(SCREEN.CLOSURE);
  }, [allAnswered, sectionIdx, sec, allAnswers, mobile, name, sessionId]);

  const handleContinueToNext = useCallback(() => {
    setSectionIdx((i) => i + 1);
    setScreen(SCREEN.INTRO);
  }, []);

  /* ── SECTION NAV BAR (shared) — clickable for navigation ── */

  // Update max section whenever sectionIdx changes
  useEffect(() => {
    setMaxSectionReached((prev) => Math.max(prev, sectionIdx));
  }, [sectionIdx]);

  const handleNavClick = useCallback((targetIdx) => {
    if (targetIdx === sectionIdx && screen === SCREEN.QUESTION) return; // already on this section's questions
    setSectionIdx(targetIdx);
    const targetSec = SECTION_CONFIG[targetIdx];
    let qs = sectionQuestionsCache[targetSec.id];
    if (!qs) {
      qs = shuffle(SECTION_QUESTIONS[targetSec.id].map((q) => ({ ...q })));
      setSectionQuestionsCache((prev) => ({ ...prev, [targetSec.id]: qs }));
    }
    setQuestions(qs);
    setQIdx(0);
    setPhase("idle");
    setScreen(SCREEN.QUESTION);
  }, [sectionIdx, sectionQuestionsCache, screen]);

  const navBar = (
    <div className="diag-nav" data-testid="diag-section-nav">
      {SECTION_NAV.map((n, i) => {
        const isClickable = i <= maxSectionReached;
        const isActive = i === sectionIdx;
        const isDone = i < sectionIdx;
        const tabStyle = isActive
          ? { backgroundColor: n.accent, color: "#fff", borderRadius: "0.35rem" }
          : isDone
          ? { backgroundColor: n.accent, color: "#fff", borderRadius: "0.35rem", opacity: 0.7 }
          : {};
        return (
          <div
            key={n.id}
            className={`diag-nav__item ${isActive ? "is-active" : ""} ${isDone ? "is-done" : ""} ${isClickable ? "is-clickable" : ""}`}
            data-testid={`diag-nav-${n.id}`}
            onClick={() => isClickable && handleNavClick(i)}
            style={{ cursor: isClickable ? "pointer" : "default", ...tabStyle }}
          >
            {n.tag} &mdash; {n.shortTitle}
          </div>
        );
      })}
    </div>
  );

  /* ═══ RENDER ═══ */

  /* ── LOGIN ── */
  if (screen === SCREEN.LOGIN) {
    // While we're checking the access token, show a soft loading state so we
    // don't flash either screen.
    if (tokenStatus === null) {
      return (
        <div className="diag-paid-shell" data-testid="diag-token-loading">
          <p className="diag-paid-desc" style={{ opacity: 0.6 }}>Loading your diagnostic&hellip;</p>
        </div>
      );
    }

    // No token (or invalid/expired) → paywall. Old OTP login is retired.
    if (tokenStatus === "denied") {
      return (
        <div className="diag-paid-shell" data-testid="diag-paywall-screen">
          <div className="diag-paid-brand" data-testid="diag-paywall-brand">
            <img src={brandAssets.mark} alt="The Pivotbox" className="diag-paid-brand__mark" />
            <span className="diag-paid-brand__wordmark">The Pivotbox</span>
          </div>
          <h1 className="diag-paid-h1" data-testid="diag-paywall-title">
            This diagnostic is <span className="diag-paid-h1__name">paid access only.</span>
          </h1>
          <h2 className="diag-paid-h2">Unlock the full Pivotbox diagnostic to begin.</h2>
          <p className="diag-paid-desc" data-testid="diag-paywall-desc">
            The full diagnostic (S1 through S2.3) is a one-time paid assessment.
            Buyers receive a private magic link by email immediately after payment.
            Already paid? Open the email we sent you and click the &ldquo;Start your diagnostic&rdquo; button.
          </p>
          <Link to="/find-out" className="diag-paid-cta" data-testid="diag-paywall-cta">
            Go to checkout &nbsp;&rarr;
          </Link>
          <p className="diag-paid-privacy">
            Lost your magic link? Message us on <a href="https://wa.me/917619300505" style={{ color: "#5DCAA5" }} target="_blank" rel="noopener noreferrer">WhatsApp</a> and we&rsquo;ll resend it.
          </p>
        </div>
      );
    }

    // Paid users (Razorpay token in URL) skip the OTP gate entirely — they're verified.
    if (accessToken) {
      const firstName = ((accessToken.name || name || "").trim().split(" ")[0]) || "";
      return (
        <div className="diag-paid-shell" data-testid="diag-paid-screen">
          <div className="diag-paid-brand" data-testid="diag-paid-brand">
            <img src={brandAssets.mark} alt="The Pivotbox" className="diag-paid-brand__mark" />
            <span className="diag-paid-brand__wordmark">The Pivotbox</span>
          </div>
          <h1 className="diag-paid-h1" data-testid="diag-paid-title">
            {firstName ? <>Welcome, <span className="diag-paid-h1__name">{firstName}</span>.</> : "Welcome."}
          </h1>
          <h2 className="diag-paid-h2">Let&rsquo;s find out where you stand.</h2>
          <p className="diag-paid-desc" data-testid="diag-paid-desc">
            Five assessments. A complete picture of your readiness, your founder profile,
            and the right first move for your specific situation. Answer honestly &mdash; the
            value of what you receive depends entirely on it.
          </p>
          <button
            type="button"
            className="diag-paid-cta"
            onClick={handlePaidStart}
            data-testid="diag-paid-start-btn"
          >
            Let&rsquo;s start &nbsp;&rarr;
          </button>
          <p className="diag-paid-privacy">Your responses are private. We do not share them.</p>
        </div>
      );
    }

    // Defensive fallback (should be unreachable now): render paywall.
    return (
      <div className="diag-paid-shell" data-testid="diag-login-shell">
        <div className="diag-login-header" data-testid="diag-login-logo">
          <img src={brandAssets.mark} alt="Pivotbox" className="diag-login-header__mark" />
        </div>
        <div className="diag-login-card" data-testid="diag-login-card">
          <div className="diag-login-card__accent" />
          {accessToken && (
            <div className="diag-premium-banner" data-testid="diag-premium-banner">
              <span className="diag-premium-banner__icon">&#10003;</span>
              <span className="diag-premium-banner__text">
                Premium access unlocked. Your diagnostic is paid &amp; reserved.
              </span>
            </div>
          )}
          <h1 className="diag-login-card__h1" data-testid="diag-login-title">Welcome.</h1>
          <h2 className="diag-login-card__h2">Let&rsquo;s find out where you stand.</h2>
          <p className="diag-login-card__desc" data-testid="diag-login-sub">
            Enter your name and WhatsApp number to receive your OTP and begin the diagnostic.
          </p>
          <div className="diag-login-card__badges">
            <span><Shield size={15} /> Secure</span>
            <span><Lock size={15} /> Private</span>
            <span><Clock size={15} /> 20 minutes</span>
          </div>
          <div className="diag-login-card__input-wrap">
            <input
              className="diag-login-card__input"
              data-testid="diag-login-name-input"
              type="text"
              maxLength={80}
              placeholder="Your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
              style={{ paddingLeft: "1.1rem" }}
              autoComplete="name"
            />
          </div>
          <div className="diag-login-card__input-wrap" style={{ marginTop: "0.7rem" }}>
            <span className="diag-login-card__prefix">+91</span>
            <input
              className="diag-login-card__input"
              data-testid="diag-login-mobile-input"
              type="tel"
              maxLength={10}
              placeholder="10-digit mobile number"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
            />
          </div>
          {loginError && <div className="diag-login-card__error" data-testid="diag-login-error">{loginError}</div>}
          <button className="diag-login-card__btn" data-testid="diag-login-send-otp-btn" onClick={handleSendOtp}>
            Send OTP on WhatsApp <ArrowRight size={18} />
          </button>
          <p className="diag-login-card__privacy">
            Your number is used only to send your result.<br />We do not share it with anyone.
          </p>
        </div>
        <div className="diag-login-tagline">CLARITY BEFORE COMMITMENT.</div>
      </div>
    );
  }

  /* ── OTP ── */
  if (screen === SCREEN.OTP) {
    return (
      <div className="diag-login-shell" data-testid="diag-otp-screen">
        <div className="diag-login-header" data-testid="diag-otp-logo">
          <img src={brandAssets.mark} alt="Pivotbox" className="diag-login-header__mark" />
        </div>
        <div className="diag-login-card">
          <div className="diag-login-card__accent" />
          <h1 className="diag-login-card__h1" data-testid="diag-otp-title">{firstName ? `Almost there, ${firstName}.` : "Enter OTP"}</h1>
          <p className="diag-login-card__desc" data-testid="diag-otp-sub">
            We have sent a 6-digit code to {mobile}.
          </p>
          <div className="diag-otp-boxes" data-testid="diag-otp-boxes">
            {otp.map((digit, i) => (
              <input key={i} ref={(el) => (otpRefs.current[i] = el)} className="diag-otp-box" data-testid={`diag-otp-box-${i}`}
                type="text" inputMode="numeric" maxLength={1} value={digit}
                onChange={(e) => handleOtpChange(i, e.target.value)} onKeyDown={(e) => handleOtpKeyDown(i, e)} />
            ))}
          </div>
          {loginError && <div className="diag-login-card__error" data-testid="diag-otp-error">{loginError}</div>}
          <button className="diag-login-card__btn" data-testid="diag-otp-verify-btn" onClick={handleVerifyOtp}>
            Verify &amp; Begin <ArrowRight size={18} />
          </button>
          <button className="diag-resend-btn" data-testid="diag-otp-resend-btn" disabled={resendTimer > 0}
            onClick={() => setResendTimer(60)}>
            {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
          </button>
        </div>
        <div className="diag-login-tagline">CLARITY BEFORE COMMITMENT.</div>
      </div>
    );
  }

  /* ── OVERVIEW ── */
  if (screen === SCREEN.OVERVIEW) {
    const OVERVIEW_DESCS = {
      s1: "Whether the structural conditions of your life \u2014 finances, family, environment, self-discipline \u2014 are actually ready to support a transition right now.",
      s2_1: "How you naturally operate when building from scratch \u2014 not what you are good at in a corporate context, but the instinctive role you step into when there is no system.",
      s2_2: "How durable you are likely to be when the work gets hard. This is what separates founders who stay in the game from those who quietly retreat after month six.",
      s2_3: "Not what you can do on a good day \u2014 what you actually do week after week. The six practical capabilities that determine whether a business survives its first year.",
    };
    const SECT_ICONS = { s1: Compass, s2_1: Link2, s2_2: ShieldCheck, s2_3: Award };
    const structuralCards = [
      { emoji: "\uD83D\uDCC8", title: "Industry Fitment", desc: "Your scores are matched against five business types: Skill-based Services, Knowledge/IP/Creator, D2C, Capital-heavy, and Tech Platform, ranked by structural fit percentage." },
    ];
    const recCards = [
      { emoji: "\uD83C\uDFAF", title: "When to Start", desc: "A direct read on whether to Launch, Parallel Build, or Prepare First \u2014 based on your financial readiness, family alignment, decision stability, and psychological durability signals.", v: "purple" },
      { emoji: "\uD83D\uDCAA", title: "Your Core Strengths & Areas to Build", desc: "The three capabilities where your profile is genuinely strong \u2014 and the three dimensions where focused attention will have the most impact, with specific actionable steps for each.", v: "purple" },
      { emoji: "\uD83D\uDE80", title: "My First Entrepreneurial Experiment", desc: "The right first experiment to run \u2014 not the right business to build. One recommendation from five options, matched to your archetype, durability signals, and strongest capabilities.", v: "green" },
    ];

    return (
      <div className="diag-ov-shell" data-testid="diag-overview-screen">
        <div className="diag-ov-inner">
          <div className="diag-ov-verified" data-testid="diag-overview-verified">
            <Check size={14} /> {firstName ? `WELCOME, ${firstName.toUpperCase()} — YOU ARE VERIFIED AND READY TO BEGIN` : "YOU ARE VERIFIED AND READY TO BEGIN"}
          </div>
          <h1 className="diag-ov-headline">
            <span className="diag-ov-headline__bold">Here is everything</span>
            <span className="diag-ov-headline__italic">this diagnostic will reveal about you.</span>
          </h1>
          <p className="diag-ov-sub">100 statements. 4 assessments. 8 outputs.</p>
          <p className="diag-ov-sub-bold">A complete picture of whether &mdash; and how &mdash; you should make this transition.</p>

          <div className="diag-ov-stats" data-testid="diag-overview-stats">
            <div><strong>100</strong><span>Statements</span></div>
            <div><strong>4</strong><span>Assessments</span></div>
            <div><strong>~20</strong><span>Minutes total</span></div>
            <div><strong>8</strong><span>Outputs</span></div>
          </div>

          <div className="diag-ov-accent" />

          <div className="diag-ov-part" data-testid="diag-overview-part1">PART 1 &mdash; THE ASSESSMENTS</div>
          <div className="diag-ov-section-head">
            <span className="diag-ov-pill diag-ov-pill--green">4 SECTIONS</span>
            <h2>What we ask you</h2>
          </div>
          <p className="diag-ov-body">
            Each assessment measures a distinct layer of your founder profile. Answer honestly &mdash; not how you hope to be, but how you actually are right now. The sections take between 4 and 6 minutes each. You cannot go back to a previous section once submitted.
          </p>

          <div className="diag-ov-assess-list" data-testid="diag-overview-assessments">
            {SECTION_CONFIG.map((s) => {
              const qc = SECTION_QUESTIONS[s.id].length;
              const Icon = SECT_ICONS[s.id] || Compass;
              return (
                <div key={s.id} className="diag-ov-assess" data-testid={`diag-overview-assess-${s.id}`}>
                  <div className="diag-ov-assess__icon" style={{ backgroundColor: s.accent }}><Icon size={20} /></div>
                  <div className="diag-ov-assess__body">
                    <div className="diag-ov-assess__top">
                      <strong>{s.tag} &mdash; {s.title}</strong>
                      <span>{qc} statements &middot; {Math.ceil(qc / 5)} min</span>
                    </div>
                    <p>{OVERVIEW_DESCS[s.id]}</p>
                    <div className="diag-ov-assess__tags">
                      {s.introCards.map((c, ci) => <span key={ci}>{c.title}</span>)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="diag-ov-divider" />

          <div className="diag-ov-part" data-testid="diag-overview-part2">PART 2 &mdash; WHAT WE DERIVE FROM YOUR RESPONSES</div>
          <div className="diag-ov-section-head">
            <span className="diag-ov-pill diag-ov-pill--purple">9 OUTPUTS</span>
            <h2>What you get back</h2>
          </div>
          <p className="diag-ov-body">
            Your responses are cross-referenced across sections to produce these outputs. None of them require additional questions &mdash; they are calculated from the 100 statements you have already answered. We review your results before sending your report.
          </p>

          <div className="diag-ov-sub-label">DIRECT ASSESSMENT RESULTS</div>
          <p className="diag-ov-body" style={{ textAlign: 'center' }}>
            You will receive scored results for each of the four assessments above &mdash; your Founder Reality Check score, your Natural Founder Archetype, your Founder Durability Score, and your Founder Capability &amp; Execution Profile.
          </p>

          <div className="diag-ov-sub-label">STRUCTURAL ANALYSIS</div>
          <div className="diag-ov-out-grid" data-testid="diag-overview-structural">
            {structuralCards.map((c, i) => (
              <div key={i} className="diag-ov-out diag-ov-out--purple">
                <span className="diag-ov-out__emoji">{c.emoji}</span>
                <strong>{c.title}</strong>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>

          <div className="diag-ov-sub-label">RECOMMENDATIONS</div>
          <div className="diag-ov-out-grid" data-testid="diag-overview-recommendations">
            {recCards.map((c, i) => (
              <div key={i} className={`diag-ov-out diag-ov-out--${c.v}`}>
                <span className="diag-ov-out__emoji">{c.emoji}</span>
                <strong>{c.title}</strong>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>

          <div className="diag-ov-sub-label" style={{ color: '#27d8e3' }}>HOW YOU RECEIVE YOUR RESULTS</div>
          <div className="diag-ov-whatsapp" data-testid="diag-overview-whatsapp">
            <span className="diag-ov-out__emoji">{"\uD83D\uDCCB"}</span>
            <strong>Final report generation</strong>
            <p>
              Once you complete all four sections, your responses are reviewed before your report is sent.
              You will receive a detailed multi-page report on this WhatsApp number covering all 8 outputs above. This is not automated &mdash; we review every result personally.
            </p>
          </div>

          <div className="diag-ov-cta" data-testid="diag-overview-cta">
            <h2 className="diag-ov-cta__h">Ready to begin?</h2>
            <p className="diag-ov-cta__italic">Let&rsquo;s find out what your profile says.</p>
            <p className="diag-ov-cta__desc">Start with S1 &mdash; Founder Reality Check. Answer honestly. The whole diagnostic takes about 20 minutes.</p>
            <button className="diag-ov-cta__btn" data-testid="diag-overview-begin-btn" onClick={() => setScreen(SCREEN.INTRO)}>
              Begin the Diagnostic <ArrowRight size={18} />
            </button>
          </div>

          <p className="diag-ov-footer-note">Your responses are saved automatically. If you close the browser mid-way, you can resume from where you left off.</p>
          <div className="diag-login-tagline" style={{ marginTop: "1.5rem" }}>CLARITY BEFORE COMMITMENT.</div>
        </div>
      </div>
    );
  }


  /* ── SECTION INTRO ── */
  if (screen === SCREEN.INTRO) {
    const qCount = SECTION_QUESTIONS[sec.id].length;
    const headlineParts = sec.introHeadline.split("|");
    const boldPart = headlineParts[0];
    const italicPart = headlineParts.length > 1 ? headlineParts.slice(1).join("|") : "";

    return (
      <div className="diag-intro-shell" style={{ backgroundColor: sec.accent }} data-testid={`diag-intro-${sec.id}`}>
        {navBar}
        <div className="diag-intro-inner">
          <div className="diag-intro-top" data-testid="diag-intro-top">
            <div className="diag-intro-logo" data-testid="diag-intro-logo">
              <img src="/assets/pivotbox/pivotbox-logo.png" alt="Pivotbox" className="diag-intro-logo__mark" />
            </div>
            <span className="diag-intro-label" data-testid="diag-intro-label">{sec.introLabel}</span>
          </div>

          <div className="diag-intro-accent-bar" />
          <h1 className="diag-intro-headline" data-testid="diag-intro-headline">
            <span className="diag-intro-headline__bold">{boldPart}</span>
            {italicPart && <span className="diag-intro-headline__italic">{italicPart}</span>}
          </h1>

          <div className="diag-intro-body" data-testid="diag-intro-body">
            {renderIntroBody(sec.introBody)}
          </div>

          <div className={`diag-intro-cards${sec.introCards.length > 4 ? " diag-intro-cards--3col" : ""}`} data-testid="diag-intro-cards">
            {sec.introCards.map((card, i) => {
              const cardTextColor = sec.id === 's1' ? '#c9a84c' : sec.id === 's2_3' ? '#ffffff' : '#1c1969';
              return (
                <div key={i} className="diag-intro-card" style={{ backgroundColor: sec.cardBg }} data-testid={`diag-intro-card-${i}`}>
                  <span className="diag-intro-card__num" style={{ color: cardTextColor }}>{String(i + 1).padStart(2, "0")}</span>
                  <strong style={{ color: cardTextColor }}>{card.name}</strong>
                  <span className="diag-intro-card__desc" style={{ color: cardTextColor }}>{card.desc}</span>
                </div>
              );
            })}
          </div>

          {/* ── BEFORE YOU BEGIN — five rules card (must appear before "Ready to begin" CTA) ── */}
          <div className="diag-intro-rules" data-testid="diag-intro-rules">
            <div className="diag-intro-rules__head">
              <span className="diag-intro-rules__eyebrow">Before you begin</span>
              <h2 className="diag-intro-rules__title">Five rules for an accurate result</h2>
              <p className="diag-intro-rules__sub">How you answer matters as much as what you answer.</p>
            </div>
            <ol className="diag-intro-rules__list">
              {[
                "Be honest. Honest answers produce useful outputs. Flattering answers produce a report that cannot help you.",
                "Answer with your first instinct \u2014 the response that comes to mind immediately, before you begin analysing or qualifying it.",
                "There is no right answer. Do not think about what you should say. Think about what is actually true.",
                "Answer as you are, not as you intend to be. Who you are today \u2014 not who you are working toward becoming.",
                "There is nothing to perform here. The assessment measures structural fit, not intelligence or ambition.",
              ].map((rule, i) => (
                <li key={i} className="diag-intro-rules__item" data-testid={`diag-intro-rule-${i + 1}`}>
                  <span className="diag-intro-rules__num">{String(i + 1).padStart(2, "0")}</span>
                  <span className="diag-intro-rules__text">{rule}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="diag-intro-meta" data-testid="diag-intro-meta">
            <span><Clock size={14} /> {qCount} statements</span>
            <span>&middot;</span>
            <span><Check size={14} /> Approximately {Math.ceil(qCount / 5)} minutes</span>
            <span>&middot;</span>
            <span>No right or wrong answers</span>
          </div>

          <div className="diag-intro-footer" data-testid="diag-intro-footer">
            <button className="diag-intro-begin-btn" data-testid="diag-intro-begin-btn" onClick={startSection}>
              Begin Assessment <ArrowRight size={18} />
            </button>
            <span className="diag-intro-footer__counter">{sectionIdx + 1} of {SECTION_CONFIG.length} &mdash; {sec.title}</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── SECTION BREAK ── */
  if (screen === SCREEN.BREAK) {
    const remaining = TOTAL_QUESTIONS - totalAnswered;
    const pctComplete = Math.round((totalAnswered / TOTAL_QUESTIONS) * 100);
    const nextSec = SECTION_CONFIG[sectionIdx + 1];
    const nextQCount = nextSec ? SECTION_QUESTIONS[nextSec.id].length : 0;
    const nextMinutes = nextSec ? Math.ceil(nextQCount / 5) : 0;
    const BREAK_ICONS = { s1: Compass, s2_1: Link2, s2_2: ShieldCheck, s2_3: Award };
    const BreakIcon = BREAK_ICONS[sec.id] || Check;

    return (
      <div className="diag-break-shell" data-testid={`diag-break-${sec.id}`}>
        <div className="diag-break-inner">
          <div className="diag-break-logo" data-testid="diag-break-logo">
            <img src={brandAssets.mark} alt="Pivotbox" className="diag-break-logo__mark" />
          </div>

          <div className="diag-break-icon" data-testid="diag-break-icon">
            <BreakIcon size={40} strokeWidth={1.5} />
          </div>

          <div className="diag-break-badge" data-testid="diag-break-badge">
            <Check size={12} /> ASSESSMENT {sectionIdx + 1} OF {SECTION_CONFIG.length} &mdash; COMPLETE
          </div>

          <h1 className="diag-break-headline" data-testid="diag-break-headline">
            {firstName && <span className="diag-break-headline__name" style={{ display: "block", fontSize: "0.95rem", letterSpacing: "0.08em", color: "#7CB89A", textTransform: "uppercase", marginBottom: "0.4rem", fontWeight: 600 }}>Nice work, {firstName}.</span>}
            {sec.title} &mdash;<br /><em>Complete.</em>
          </h1>
          <div className="diag-break-rule" />

          <div className="diag-break-saved" data-testid="diag-break-saved">
            <Check size={14} /> {sec.breakSavedMsg || "Your responses have been saved."}
          </div>

          <div className="diag-break-progress" data-testid="diag-break-progress">
            <div className="diag-break-progress__header">
              <span>Your progress</span>
              <span>{totalAnswered} of {TOTAL_QUESTIONS} statements complete</span>
            </div>
            <div className="diag-break-progress__bar">
              <div className="diag-break-progress__fill" style={{ width: `${pctComplete}%` }} />
            </div>

            <div className="diag-break-stepper" data-testid="diag-break-stepper">
              {SECTION_CONFIG.map((s, i) => (
                <div key={s.id} className="diag-break-stepper__item">
                  <div className={`diag-break-stepper__dot ${i <= sectionIdx ? "is-done" : ""} ${i === sectionIdx + 1 ? "is-next" : ""}`}>
                    {i <= sectionIdx ? <Check size={14} /> : s.tag}
                  </div>
                  <span className={i <= sectionIdx ? "is-done" : ""}>{s.tag}</span>
                  {i < SECTION_CONFIG.length - 1 && <div className="diag-break-stepper__line" />}
                </div>
              ))}
            </div>
          </div>

          {nextSec && (
            <div className="diag-break-next-card" data-testid="diag-break-next-card">
              <span className="diag-break-next-card__label">UP NEXT</span>
              <span className="diag-break-next-card__time" data-testid="diag-break-next-time"><Clock size={12} /> {nextMinutes} minutes</span>
              <strong>{nextSec.title}</strong>
              <span className="diag-break-next-card__info">{nextSec.breakPreview || `${nextQCount} statements`}</span>
            </div>
          )}

          <button className="diag-break-continue-btn" data-testid="diag-break-continue-btn" onClick={handleContinueToNext}>
            Continue to Next Assessment <ArrowRight size={18} />
          </button>
          <button className="diag-break-prev-btn" data-testid="diag-break-prev-btn" onClick={() => handleNavClick(sectionIdx)}>
            <ArrowLeft size={16} /> Go to Previous Assessment
          </button>
          <div className="diag-login-tagline" style={{ marginTop: "2rem" }}>CLARITY BEFORE COMMITMENT.</div>
        </div>
      </div>
    );
  }

  /* ── COMPLETION ── */
  if (screen === SCREEN.COMPLETE) {
    const BREAK_ICONS = { s1: Compass, s2_1: Link2, s2_2: ShieldCheck, s2_3: Award };
    const BreakIcon = BREAK_ICONS[sec.id] || Award;

    return (
      <div className="diag-break-shell" data-testid="diag-complete-screen">
        <div className="diag-break-inner">
          <div className="diag-break-logo" data-testid="diag-complete-logo">
            <img src={brandAssets.mark} alt="Pivotbox" className="diag-break-logo__mark" />
          </div>

          <div className="diag-break-icon" data-testid="diag-complete-icon">
            <BreakIcon size={40} strokeWidth={1.5} />
          </div>

          <div className="diag-break-badge" data-testid="diag-complete-badge">
            <Check size={12} /> ASSESSMENT {SECTION_CONFIG.length} OF {SECTION_CONFIG.length} &mdash; COMPLETE
          </div>

          <h1 className="diag-break-headline" data-testid="diag-complete-headline">
            {sec.title} &mdash;<br /><em>Complete.</em>
          </h1>
          <div className="diag-break-rule" />

          <div className="diag-break-saved" data-testid="diag-complete-saved">
            <Check size={14} /> Your full diagnostic has been completed.
          </div>

          <div className="diag-break-progress" data-testid="diag-complete-progress">
            <div className="diag-break-progress__header">
              <span>Your progress</span>
              <span>{TOTAL_QUESTIONS} of {TOTAL_QUESTIONS} statements complete</span>
            </div>
            <div className="diag-break-progress__bar">
              <div className="diag-break-progress__fill" style={{ width: "100%" }} />
            </div>

            <div className="diag-break-stepper" data-testid="diag-complete-stepper">
              {SECTION_CONFIG.map((s, i) => (
                <div key={s.id} className="diag-break-stepper__item">
                  <div className="diag-break-stepper__dot is-done">
                    <Check size={14} />
                  </div>
                  <span className="is-done">{s.tag}</span>
                  {i < SECTION_CONFIG.length - 1 && <div className="diag-break-stepper__line" />}
                </div>
              ))}
            </div>
          </div>

          <div className="diag-break-next-card" data-testid="diag-complete-next-card">
            <span className="diag-break-next-card__label">WHAT HAPPENS NEXT</span>
            <strong>Your Complete Pivotbox Report</strong>
            <span className="diag-break-next-card__info">
              We will review your responses and share your complete report on WhatsApp.
              100 statements across 4 assessments &mdash; your signals, your profile, and your capability map.
            </span>
          </div>

          <div className="diag-login-tagline" style={{ marginTop: "2rem" }}>CLARITY BEFORE COMMITMENT.</div>
        </div>
      </div>
    );
  }

  /* ── CLOSURE ── */
  if (screen === SCREEN.CLOSURE) {
    const CL_A_ICONS = { s1: Compass, s2_1: Link2, s2_2: ShieldCheck, s2_3: Award };
    const CL_A_COLORS = { s1: "#256847", s2_1: "#ba632f", s2_2: "#964f18", s2_3: "#2f4a75" };
    const CL_B_ICONS = [BarChart2, Building2, Clock, Zap, Rocket];
    const CL_B_COLORS = ["#e040a0", "#b85c38", "#7c3aed", "#f59e0b", "#ec4899"];

    return (
      <div className="diag-closure-shell" data-testid="diag-closure-screen">
        <div className="diag-closure-inner">
          <div className="diag-closure-logo" data-testid="diag-closure-logo">
            <img src="/assets/pivotbox/pivotbox-logo.png" alt="Pivotbox" className="diag-closure-logo__mark" />
          </div>

          <div className="diag-closure-trophy" data-testid="diag-closure-trophy">
            <Trophy size={44} strokeWidth={1.5} />
          </div>

          <div className="diag-closure-badge" data-testid="diag-closure-badge">
            <Check size={12} /> {firstName ? `${firstName.toUpperCase()} — ALL 4 ASSESSMENTS COMPLETE` : "All 4 assessments — complete"}
          </div>

          <h1 className="diag-closure-headline" data-testid="diag-closure-headline">
            <span className="diag-closure-headline__bold">100 statements{firstName ? `, ${firstName}.` : "."}</span>
            <span className="diag-closure-headline__italic">Here is what you have unlocked.</span>
          </h1>

          <p className="diag-closure-desc" data-testid="diag-closure-desc">
            Your responses across all four assessments have been saved. <strong>Here</strong> is exactly what your report will cover &mdash; and what each output means for your transition.
          </p>

          <div className="diag-closure-section-label" data-testid="diag-closure-section-a-label">Section A</div>
          <h2 className="diag-closure-section-title">Results of the tests you took</h2>
          <p className="diag-closure-section-sub">Four scored profiles &mdash; one for each assessment</p>

          <div className="diag-closure-cards" data-testid="diag-closure-section-a-cards">
            {CLOSURE_A_CARDS.map((card) => {
              const Icon = CL_A_ICONS[card.sectionId] || Compass;
              const color = CL_A_COLORS[card.sectionId] || "#256847";
              return (
                <div key={card.sectionId} className="diag-closure-card" data-testid={`diag-closure-card-a-${card.sectionId}`}>
                  <div className="diag-closure-card__icon" style={{ backgroundColor: color }}>
                    <Icon size={20} />
                  </div>
                  <div className="diag-closure-card__body">
                    <span className="diag-closure-card__tag">{card.tag}</span>
                    <strong>{card.title}</strong>
                    <p>{card.desc}</p>
                  </div>
                  <div className="diag-closure-card__check">
                    <Check size={14} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="diag-closure-section-label" data-testid="diag-closure-section-b-label">Section B</div>
          <h2 className="diag-closure-section-title">What we derive from your responses</h2>
          <p className="diag-closure-section-sub">Five conclusions calculated from your 100 answers &mdash; no additional questions</p>

          <div className="diag-closure-cards" data-testid="diag-closure-section-b-cards">
            {CLOSURE_B_CARDS.map((card, i) => {
              const Icon = CL_B_ICONS[i];
              return (
                <div key={i} className="diag-closure-card diag-closure-card--b" data-testid={`diag-closure-card-b-${i}`}>
                  <div className="diag-closure-card__icon" style={{ backgroundColor: CL_B_COLORS[i] }}>
                    <Icon size={20} />
                  </div>
                  <div className="diag-closure-card__body">
                    <span className="diag-closure-card__tag">{card.tag}</span>
                    <strong>{card.title}</strong>
                    <p><span className="diag-closure-card__answers">Answers:</span> {card.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="diag-closure-delivery" data-testid="diag-closure-delivery">
            <div className="diag-closure-delivery__via" data-testid="diag-closure-delivery-via">
              <MessageCircle size={14} /> Delivery via WhatsApp
            </div>
            <h2 className="diag-closure-delivery__headline">
              We will review your responses<br />
              <em>and share your report shortly.</em>
            </h2>
            <p className="diag-closure-delivery__body">
              Every result is reviewed personally before your report is sent. This is not automated. Your report will cover all eight outputs above &mdash; scored, interpreted, and written specifically for your profile.
            </p>
            <div className="diag-closure-delivery__badges" data-testid="diag-closure-delivery-badges">
              <span><FileText size={14} /> Multi-page WhatsApp report</span>
              <span><Target size={14} /> Personalised to your responses</span>
              <span><Eye size={14} /> Reviewed before sending</span>
            </div>
            <div className="diag-closure-delivery__tagline">Clarity before commitment.</div>
            <div className="diag-closure-delivery__footer">The Pivotbox</div>
          </div>

          {/* ── Calendly Booking Section ── */}
          <div className="diag-closure-calendly" data-testid="diag-closure-calendly">
            <div className="diag-closure-calendly__badge">NEXT STEP</div>
            <h2 className="diag-closure-calendly__headline">Book your advisory session</h2>
            <p className="diag-closure-calendly__body">
              Your assessment data feeds into a personalised 60-minute advisory session where we cover two critical stages:
            </p>
            <div className="diag-closure-calendly__cards">
              <div className="diag-closure-calendly__card diag-closure-calendly__card--s3">
                <span className="diag-closure-calendly__card-tag">S3 &mdash; Strategy</span>
                <p className="diag-closure-calendly__card-title">Founder Strategy Blueprint</p>
                <p className="diag-closure-calendly__card-desc">We examine your business idea across six dimensions &mdash; market opportunity, revenue model, competition, operations, funding, and your 12-month transition plan. You leave with a grounded understanding of what the business requires.</p>
              </div>
              <div className="diag-closure-calendly__card diag-closure-calendly__card--s4">
                <span className="diag-closure-calendly__card-tag">S4 &mdash; Shift</span>
                <p className="diag-closure-calendly__card-title">Founder Operating System</p>
                <p className="diag-closure-calendly__card-desc">We build your personal operating system for year one &mdash; weekly rhythm, revenue tracking, decision filters, energy management, and a quarter-by-quarter focus model. Structured around how you specifically work.</p>
              </div>
            </div>
            <a
              href="https://calendly.com/suhasm19/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="diag-closure-calendly__btn"
              data-testid="diag-closure-calendly-btn"
            >
              Book your 60-min session &rarr;
            </a>
          </div>
        </div>
      </div>
    );
  }

  /* ── QUESTION SCREEN ── */
  const currentAnswered = currentQ && sectionAnswers[currentQ.code] != null;
  const nextDisabled = qIdx >= questions.length - 1 || phase !== "idle" || !currentAnswered;
  const orangeStyle = { backgroundColor: "#d98825", color: "#fff", borderColor: "#d98825" };
  const canGoBack = qIdx > 0 && phase === "idle";

  // Find last answered question index in this section
  const lastAnsweredIdx = (() => {
    for (let i = questions.length - 1; i >= 0; i--) {
      if (sectionAnswers[questions[i].code] != null) return i;
    }
    return -1;
  })();

  return (
    <div className="diag-question-shell" data-testid={`diag-questions-${sec.id}`}>
      {navBar}
      <div className="diag-question-wrapper">
        <div className="diag-card" style={{ backgroundColor: sec.accent }} data-testid="diag-card">
          <div className="diag-card__header" data-testid="diag-card-header">
            <div className="diag-card__title" data-testid="diag-card-title">{sec.title}</div>
            <div className="diag-card__progress-group">
              <div className="diag-card__progress-track" data-testid="diag-progress-track">
                <div className="diag-card__progress-fill" style={{ width: `${progress}%`, backgroundColor: sec.accentLight }} data-testid="diag-progress-fill" />
              </div>
              <div className="diag-card__progress-label" data-testid="diag-progress-label">Q {Math.min(qIdx + 1, questions.length)} of {questions.length}</div>
            </div>
          </div>

          {/* Clickable question dot strip */}
          <div className="diag-card__qdots" data-testid="diag-card-qdots">
            {questions.map((q, i) => {
              const answered = sectionAnswers[q.code] != null;
              const isCurrent = i === qIdx;
              return (
                <button
                  key={q.code}
                  className={`diag-card__qdot ${isCurrent ? "is-current" : ""} ${answered ? "is-answered" : ""}`}
                  data-testid={`diag-qdot-${i + 1}`}
                  onClick={() => { if (answered || isCurrent) { setQIdx(i); setPhase("idle"); } }}
                  style={isCurrent ? { backgroundColor: "#d98825" } : answered ? { backgroundColor: sec.accentLight } : undefined}
                  disabled={!answered && !isCurrent}
                  title={`Q${i + 1}${answered ? ` (answered: ${sectionAnswers[q.code]})` : ""}`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          <div className="diag-card__body" data-testid="diag-card-body">
            <div className="diag-card__topbar" data-testid="diag-card-topbar">
              <div className="diag-card__qcount" style={{ color: "#d98825" }} data-testid="diag-card-qcount">Question {qIdx + 1}</div>
              <div className="diag-card__nav" data-testid="diag-card-nav">
                <button className="diag-card__nav-btn" data-testid="diag-card-back-btn" disabled={!canGoBack} onClick={handleBack} style={canGoBack ? orangeStyle : undefined}><ArrowLeft size={16} /> Back</button>
                <button className="diag-card__nav-btn" data-testid="diag-card-next-btn" disabled={nextDisabled} onClick={handleNext} style={currentAnswered && qIdx < questions.length - 1 ? orangeStyle : undefined}>Next <ArrowRight size={16} /></button>
              </div>
            </div>

            <div className={`diag-card__q-shell ${phase !== "idle" ? `is-${phase}` : ""}`} data-testid="diag-card-q-shell">
              {currentQ && (
                <>
                  <p className="diag-card__q-text" data-testid="diag-card-q-text">{currentQ.text}</p>
                  <div className="diag-card__scale-labels" data-testid="diag-card-scale-labels">
                    <span>1 &mdash; Strongly Disagree</span>
                    <span>Strongly Agree &mdash; 5</span>
                  </div>
                  <div className="diag-card__rating-grid" data-testid="diag-card-rating-grid">
                    {[1, 2, 3, 4, 5].map((r) => {
                      const selected = sectionAnswers[currentQ.code] === r;
                      return (
                        <button key={r} className={`diag-card__rate-btn ${selected ? "is-selected" : ""}`} data-testid={`diag-rate-btn-${r}`}
                          style={selected ? { backgroundColor: "#ffffff", color: sec.accent } : undefined} onClick={() => handleRate(r)}>
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="diag-card__footer" data-testid="diag-card-footer">
              {maxSectionReached > 0 && (
                <div className="diag-card__footer-left">
                  <div className="diag-card__prev-sections" data-testid="diag-card-prev-sections">
                    {Array.from({ length: maxSectionReached + 1 }, (_, i) => i)
                      .filter((i) => i !== sectionIdx)
                      .map((i) => (
                      <button
                        key={SECTION_NAV[i].tag}
                        className="diag-card__prev-section-btn"
                        data-testid={`diag-card-prev-section-${SECTION_NAV[i].id}`}
                        onClick={() => handleNavClick(i)}
                        type="button"
                        style={{ backgroundColor: SECTION_NAV[i].accent, color: "#fff", borderColor: SECTION_NAV[i].accent }}
                      >
                        {SECTION_NAV[i].tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button className={`diag-card__submit-btn ${allAnswered ? "is-active" : ""}`} data-testid="diag-card-submit-btn" disabled={!allAnswered} onClick={handleSubmitSection}>
                {sectionIdx < SECTION_CONFIG.length - 1 ? "Submit & Continue" : "Submit & Finish"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
